import { useEffect, useMemo, useState } from "react";
import classes from "./jobs.module.css";
import useAuth from "../../../../hooks/useAuth";
import { useDispatch, useSelector } from "react-redux";
import {
  acceptProposal,
  getJobProposals,
  getJobs,
  getSelfProposal,
} from "../../../../redux/slices/jobPostingSlice";
import { Pagination } from "@mui/material";
import { formatToPrettyDate } from "../../../../utils";

const JobProposals = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("myJobs");
  const [postedJobPage, setPostedJobPage] = useState(1);
  const [myProposalPage, setMyProposalPage] = useState(1);
  const user = useAuth();
  const [activeJobId, setActiveJobId] = useState(null);
  const {
    jobProposalsLoading,
    jobsProposalError,
    jobProposals,
    jobs,
    totalJobs,
    selfProposals,
    selfProposalLoading,
    selfProposalError,
    selfTotalProposals,
  } = useSelector((state) => state.job);
  useEffect(() => {
    dispatch(getJobs({ my_jobs: true, page_number: postedJobPage }));
  }, [postedJobPage]);

  useEffect(() => {
    dispatch(getSelfProposal({ page_number: myProposalPage }));
  }, [myProposalPage]);

  // Mock data - replace with your actual data fetching logic

  const pages = useMemo(() => {
    return totalJobs % 10 === 0
      ? totalJobs / 10
      : Math.floor(totalJobs / 10) + 1;
  }, [totalJobs]);

  const proposalPages = useMemo(() => {
    return selfTotalProposals % 10 === 0
      ? selfTotalProposals / 10
      : Math.floor(selfTotalProposals / 10) + 1;
  }, [selfTotalProposals]);

  // Mock data for proposals you've made to other people's jobs
  const myProposals = [
    {
      id: 201,
      jobTitle: "Corporate Event Photographer",
      clientName: "TechStart Inc.",
      proposedPrice: "$600",
      status: "Pending",
      submittedDate: "May 13, 2025",
      jobDetails: {
        id: 301,
        budget: "$500-800",
        date: "June 5, 2025",
        location: "Chicago, IL",
        description:
          "Seeking a professional photographer for our annual company retreat. Approximately 6 hours of coverage needed.",
      },
    },
    {
      id: 202,
      jobTitle: "Band for Restaurant Opening",
      clientName: "Fusion Bites",
      proposedPrice: "$1200",
      status: "Accepted",
      submittedDate: "May 8, 2025",
      jobDetails: {
        id: 302,
        budget: "$1000-1500",
        date: "May 30, 2025",
        location: "Austin, TX",
        description:
          "Looking for a jazz band to perform at our restaurant grand opening. 3-4 hours of performance.",
      },
    },
    {
      id: 203,
      jobTitle: "Wedding Vocalist",
      clientName: "Emma & James",
      proposedPrice: "$450",
      status: "Declined",
      submittedDate: "May 5, 2025",
      jobDetails: {
        id: 303,
        budget: "$400-600",
        date: "July 12, 2025",
        location: "Miami, FL",
        description:
          "Need a vocalist for our wedding ceremony. Must know traditional wedding songs and be able to work with our pianist.",
      },
    },
  ];

  const handleChange = (event, value) => {
    setPostedJobPage(value);
  };
  const handleProposalPageChange = (event, value) => {
    setMyProposalPage(value);
  };

  const toggleJobDetails = (jobId) => {
    setActiveJobId(activeJobId === jobId ? null : jobId);
    if (activeJobId !== jobId && jobId !== null) {
      dispatch(
        getJobProposals({
          job_id: jobId,
        })
      );
    }
  };

  const handleAcceptProposal = (proposalId) => {
    dispatch(
      acceptProposal({
        proposal_id: proposalId,
      })
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h2>Jobs & Proposals</h2>
        {user?.type === "user" && (
          <button className={classes.postJobButton}>Post a New Job</button>
        )}
      </div>

      <div className={classes.tabs}>
        <button
          className={`${classes.tabButton} ${
            activeTab === "myJobs" ? classes.activeTab : ""
          }`}
          onClick={() => {
            setActiveTab("myJobs");
          }}
        >
          My Posted Jobs
        </button>
        <button
          className={`${classes.tabButton} ${
            activeTab === "myProposals" ? classes.activeTab : ""
          }`}
          onClick={() => {
            dispatch(getSelfProposal({ page_number: myProposalPage }));
            setActiveTab("myProposals");
          }}
        >
          My Proposals
        </button>
      </div>

      {activeTab === "myJobs" ? (
        jobs?.length === 0 ? (
          <div className={classes.emptyState}>
            <p>
              You haven't posted any jobs yet. Create a new job to receive
              proposals from artists.
            </p>
          </div>
        ) : (
          <>
            <div className={classes.jobsList}>
              {jobs?.map((job) => (
                <div key={job.id} className={classes.jobCard}>
                  <div
                    className={classes.jobHeader}
                    onClick={() => toggleJobDetails(job.id)}
                  >
                    <div className={classes.jobInfo}>
                      <h3>{job?.title}</h3>
                      <div className={classes.jobMeta}>
                        <span
                          className={classes.budget}
                        >{`${job?.budget?.min} - ${job?.budget?.max} INR`}</span>
                        <span className={classes.date}>{job?.date}</span>
                        <span className={classes.location}>
                          {job?.address?.length < 15
                            ? job?.address
                            : job?.address?.substring(0, 15) + "..."}
                        </span>
                      </div>
                    </div>
                    <div className={classes.jobStatus}>
                      {/* <span
                      className={`${classes.statusBadge} ${
                        classes[job.status.toLowerCase()]
                      }`}
                    >
                      {job.status}
                    </span> */}
                      <span className={classes.proposalCount}>
                        {job?.proposal_count} proposal
                        {job?.proposal_count !== 1 ? "s" : ""}
                      </span>
                      <span className={classes.expandIcon}>
                        {activeJobId === job.id ? "▲" : "▼"}
                      </span>
                    </div>
                  </div>

                  {activeJobId === job?.id && (
                    <div className={classes.jobContent}>
                      <div className={classes.jobDescription}>
                        <h4>Description</h4>
                        <p>{job.description}</p>
                      </div>

                      <div className={classes.proposalsSection}>
                        <h4>Proposals Received</h4>
                        {jobProposals?.length === 0 ? (
                          <p className={classes.noProposals}>
                            No proposals yet. Check back later.
                          </p>
                        ) : !jobProposalsLoading ? (
                          <div className={classes.proposalsList}>
                            {jobProposals?.map((proposal) => (
                              <div
                                key={proposal.id}
                                className={classes.proposalCard}
                              >
                                <div className={classes.artistInfo}>
                                  <div className={classes.artistAvatar}>
                                    <img
                                      height={40}
                                      width={40}
                                      src={
                                        proposal?.created_by_details
                                          ?.displayPictureUrl ||
                                        proposal?.created_by_details
                                          ?.display_picture_thumbnail
                                      }
                                      alt="artist_picture"
                                    />
                                  </div>
                                  <div>
                                    <h5>
                                      {proposal?.created_by_details?.username}
                                    </h5>
                                    <div className={classes.rating}>
                                      {Math.ceil(Math.random(3, 5) * 10)}
                                      <span className={classes.ratingValue}>
                                        {proposal.rating}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className={classes.proposalDetails}>
                                  <span className={classes.price}>
                                    {proposal.price}
                                  </span>
                                  <div className={classes.proposalActions}>
                                    {job?.status === "accepted" ? (
                                      <button className={classes.acceptButton}>
                                        Accepted Proposal
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          handleAcceptProposal(proposal?.id);
                                        }}
                                        className={classes.acceptButton}
                                      >
                                        Accept
                                      </button>
                                    )}
                                    {/* <button className={classes.declineButton}>
                                      Decline
                                    </button> */}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span>Loading Details...</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className={classes.pagination}>
              <Pagination
                count={pages}
                page={postedJobPage}
                onChange={handleChange}
                color="secondary"
              />
            </div>
          </>
        )
      ) : selfProposals?.length === 0 ? (
        <div className={classes.emptyState}>
          <p>
            You haven't submitted any proposals yet. Browse available jobs to
            start bidding.
          </p>
        </div>
      ) : (
        <div className={classes.jobsList}>
          {selfProposals?.map((proposal) => (
            <div key={proposal.id} className={classes.jobCard}>
              <div
                className={classes.jobHeader}
                onClick={() => toggleJobDetails(proposal?.id)}
              >
                <div className={classes.jobInfo}>
                  <h3>{proposal.jobTitle}</h3>
                  <div className={classes.jobMeta}>
                    <span className={classes.client}>
                      Client: {proposal?.created_by_details?.username}
                    </span>
                    <span className={classes.price}>
                      Your Bid: {proposal?.price}
                    </span>
                  </div>
                </div>
                <div className={classes.jobStatus}>
                  <span
                    className={`${classes.statusBadge} ${
                      classes[proposal?.status]
                    }`}
                  >
                    {proposal?.status}
                  </span>
                  <span className={classes.submittedDate}>
                    Submitted:{formatToPrettyDate(proposal?.createdAt)}
                  </span>
                  <span className={classes.expandIcon}>
                    {activeJobId === proposal.id ? "▲" : "▼"}
                  </span>
                </div>
              </div>

              {activeJobId === proposal.id && (
                <div className={classes.jobContent}>
                  {/* <div className={classes.jobDescription}>
                    <h4>Job Details</h4>
                    <p>
                      <strong>Budget:</strong> {proposal.jobDetails.budget}
                    </p>
                    <p>
                      <strong>Date:</strong> {proposal.jobDetails.date}
                    </p>
                    <p>
                      <strong>Location:</strong> {proposal.jobDetails.location}
                    </p>
                    <p>
                      <strong>Description:</strong>{" "}
                      {proposal.jobDetails.description}
                    </p>
                  </div> */}

                  <div className={classes.proposalView}>
                    <h4>Your Proposal</h4>
                    <div className={classes.myProposalDetails}>
                      <p>
                        <strong>Proposed Amount:</strong>{" "}
                        {proposal?.price + " INR"}
                      </p>
                      <p>
                        <strong>Status:</strong> {proposal?.status}
                      </p>
                      <p>
                        <strong>Submitted on:</strong>{" "}
                        {formatToPrettyDate(proposal?.createdAt)}
                      </p>

                      <div className={classes.proposalActions}>
                        {/* {proposal.status === "Pending" && (
                          <button className={classes.editButton}>
                            Edit Proposal
                          </button>
                        )}
                        {proposal.status === "Accepted" && (
                          <button className={classes.messageButton}>
                            Message Client
                          </button>
                        )} */}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          <div className={classes.pagination}>
            <Pagination
              count={proposalPages}
              page={myProposalPage}
              onChange={handleProposalPageChange}
              color="secondary"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobProposals;
